import { literalValues } from "@shared/validation/literal-values";

export const COMPANY_ONBOARDING_STATUS = {
  NOT_ON_REPACKIFY: "not_on_repackify",
  INVITED: "invited",
  SIGNED_IN: "signed_in",
  SET_UP: "set_up",
} as const;

export const COMPANY_ONBOARDING_STATUSES = literalValues(COMPANY_ONBOARDING_STATUS);

export type CompanyOnboardingStatus = (typeof COMPANY_ONBOARDING_STATUSES)[number];

export const COMPANY_ONBOARDING_STATUS_LABELS: Record<CompanyOnboardingStatus, string> = {
  not_on_repackify: "No sign-ins yet",
  invited: "Invited",
  signed_in: "Signed in",
  set_up: "Set up",
};

export const ONBOARDING_STEP = {
  CAN_LOG_IN: "can_log_in",
  BUSINESS_DETAILS: "business_details",
  SITE_DETAILS: "site_details",
  LISTINGS_LIVE: "listings_live",
  W9: "w9",
  LISTING_PHOTOS: "listing_photos",
  PAYOUTS: "payouts",
  BILLING: "billing",
  CREDIT_APPLICATION: "credit_application",
  TAX_CERTIFICATE: "tax_certificate",
  FIRST_PURCHASE_ORDER: "first_purchase_order",
  FIRST_ORDER_PAID: "first_order_paid",
} as const;

export const ONBOARDING_STEPS = literalValues(ONBOARDING_STEP);

export type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number];

export const ONBOARDING_STEP_LABELS: Record<OnboardingStepKey, string> = {
  can_log_in: "Can log in",
  business_details: "Business details",
  site_details: "Dock and site details",
  listings_live: "Listings live",
  w9: "W-9 on file",
  listing_photos: "Listing photos",
  payouts: "Gets paid by Stripe",
  billing: "Billing set up",
  credit_application: "Credit application",
  tax_certificate: "Tax-exempt certificate",
  first_purchase_order: "First purchase order accepted on Repackify",
  first_order_paid: "First order paid on Repackify",
};

export const ONBOARDING_REQUEST_LABELS: Partial<Record<OnboardingStepKey, string>> = {
  business_details: "Your business details",
  site_details: "Your dock and site details",
  w9: "A signed W-9",
  payouts: "The bank account we pay you to",
  listing_photos: "Photos for your listings",
  credit_application: "A credit application, for payment terms",
  tax_certificate: "Your tax-exempt certificate",
};

export function onboardingRequestLabel(key: OnboardingStepKey): string {
  return ONBOARDING_REQUEST_LABELS[key] ?? ONBOARDING_STEP_LABELS[key];
}

export const ONBOARDING_LINK_STEPS = {
  vendor: [
    ONBOARDING_STEP.BUSINESS_DETAILS,
    ONBOARDING_STEP.SITE_DETAILS,
    ONBOARDING_STEP.W9,
    ONBOARDING_STEP.PAYOUTS,
    ONBOARDING_STEP.LISTING_PHOTOS,
  ],
  buyer: [
    ONBOARDING_STEP.BUSINESS_DETAILS,
    ONBOARDING_STEP.SITE_DETAILS,
    ONBOARDING_STEP.CREDIT_APPLICATION,
    ONBOARDING_STEP.TAX_CERTIFICATE,
  ],
} as const satisfies Record<"vendor" | "buyer", ReadonlyArray<OnboardingStepKey>>;

export const ONBOARDING_HIDDEN_WHEN_DONE_STEPS: ReadonlyArray<OnboardingStepKey> = [ONBOARDING_STEP.LISTING_PHOTOS];

export const ONBOARDING_REQUESTED_ONLY_STEPS: ReadonlyArray<OnboardingStepKey> = [
  ONBOARDING_STEP.CREDIT_APPLICATION,
  ONBOARDING_STEP.TAX_CERTIFICATE,
];

export const ONBOARDING_PATH = "/dashboard/onboarding";

export const SETUP_STEP_DONE_ACTION = "setup_step_done";

export const ONBOARDING_SIDE = {
  REPACKIFY: "repackify",
  CUSTOMER: "customer",
} as const;

export const ONBOARDING_SIDES = literalValues(ONBOARDING_SIDE);

export type OnboardingSide = (typeof ONBOARDING_SIDES)[number];

export const PERSON_ACCESS = {
  CAN_LOG_IN: "can_log_in",
  INVITED: "invited",
  ASKED_TO_JOIN: "asked_to_join",
  NO_LOGIN: "no_login",
} as const;

export const PERSON_ACCESS_STATES = literalValues(PERSON_ACCESS);

export const ONBOARDING_LIMITS = {
  members: 100,
  contacts: 100,
  invitations: 20,
  claims: 20,
  joinRequests: 20,
  orderScan: 100,
  repackifyListingScan: 50,
  userMemberships: 20,
  listingPhotoScan: 25,
  documentScan: 25,
} as const;
